/**
 * BigDecimal utility functions
 *
 * Safe helper functions for working with BigDecimal instances,
 * handling null/undefined values gracefully.
 */

import type { BigDecimal } from "@solomon-labs/bigdecimal";

/**
 * Check if a value is a valid BigDecimal instance
 * Note: Only check for essential methods that exist on all BigDecimal instances
 */
export function isValidBigDecimal(value: unknown): value is BigDecimal {
    return (
        value !== null &&
        value !== undefined &&
        typeof value === "object" &&
        typeof (value as BigDecimal).toNumber === "function"
    );
}

/**
 * Safe isZero check - works with all BigDecimal instances
 * Returns true if value is null, undefined, or zero
 */
export function isZero(value: BigDecimal | null | undefined): boolean {
    if (!value || !isValidBigDecimal(value)) return true;
    return value.eq(0);
}

/**
 * Safe isNegative check - works with all BigDecimal instances
 * Returns false if value is null or undefined
 */
export function isNegative(value: BigDecimal | null | undefined): boolean {
    if (!value || !isValidBigDecimal(value)) return false;
    return value.lt(0);
}
